import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Dices, Copy, Check, Loader2, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ResultRow, getGrupoFromMilhar, animalNames, animalEmojis } from "./constants";
import { salvarPalpites } from "./palpitesUtils";

interface GerarJogosEstatisticasProps {
  filtered: ResultRow[];
  source: 'rio' | 'federal';
  filtrosGeracao: Record<string, unknown>;
}

const GerarJogosEstatisticas: React.FC<GerarJogosEstatisticasProps> = ({ filtered, source, filtrosGeracao }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const jogos = useMemo(() => {
    if (filtered.length === 0) return null;

    // Count frequencies
    const milharCount: Record<string, number> = {};
    const centenaCount: Record<string, number> = {};
    const dezenaCount: Record<string, number> = {};
    const grupoCount: Record<number, number> = {};

    filtered.forEach(r => {
      milharCount[r.milhar] = (milharCount[r.milhar] || 0) + 1;
      const cent = r.milhar.slice(-3);
      centenaCount[cent] = (centenaCount[cent] || 0) + 1;
      const dez = r.milhar.slice(-2);
      dezenaCount[dez] = (dezenaCount[dez] || 0) + 1;
      grupoCount[r.grupo] = (grupoCount[r.grupo] || 0) + 1;
    });

    const sortedMilhares = Object.entries(milharCount).sort((a, b) => b[1] - a[1]);
    const sortedCentenas = Object.entries(centenaCount).sort((a, b) => b[1] - a[1]);
    const sortedDezenas = Object.entries(dezenaCount).sort((a, b) => b[1] - a[1]);
    const sortedGrupos = Object.entries(grupoCount)
      .map(([g, c]) => ({ grupo: parseInt(g), count: c }))
      .sort((a, b) => b.count - a.count);

    // Milhar: Top 5 + 5 random weighted
    const topMilhares = sortedMilhares.slice(0, 5).map(([m]) => m);
    const remainingMilhares = sortedMilhares.slice(5);
    const randomMilhares: string[] = [];
    if (remainingMilhares.length > 0) {
      const totalWeight = remainingMilhares.reduce((sum, [, c]) => sum + c, 0);
      const used = new Set(topMilhares);
      let attempts = 0;
      while (randomMilhares.length < Math.min(5, remainingMilhares.length) && attempts < 50) {
        let rand = Math.random() * totalWeight;
        for (const [m, c] of remainingMilhares) {
          rand -= c;
          if (rand <= 0 && !used.has(m)) {
            randomMilhares.push(m);
            used.add(m);
            break;
          }
        }
        attempts++;
      }
    }

    // Centena: Top 10
    const centenas = sortedCentenas.slice(0, 10).map(([c]) => c);

    // Dezena: Top 10
    const dezenas = sortedDezenas.slice(0, 10).map(([d]) => d);

    // Duque de Dezena: 5 pairs from top dezenas
    const topDez = sortedDezenas.slice(0, 10).map(([d]) => d);
    const duquesDezena: string[] = [];
    for (let i = 0; i < topDez.length && duquesDezena.length < 5; i++) {
      for (let j = i + 1; j < topDez.length && duquesDezena.length < 5; j++) {
        duquesDezena.push(`${topDez[i]}-${topDez[j]}`);
      }
    }

    // Duque de Grupo: 5 pairs from top groups
    const topGrp = sortedGrupos.slice(0, 10);
    const dupGrupo: string[] = [];
    for (let i = 0; i < topGrp.length && dupGrupo.length < 5; i++) {
      for (let j = i + 1; j < topGrp.length && dupGrupo.length < 5; j++) {
        const g1 = topGrp[i].grupo;
        const g2 = topGrp[j].grupo;
        dupGrupo.push(`G${String(g1).padStart(2, '0')} ${animalEmojis[g1] || ''} + G${String(g2).padStart(2, '0')} ${animalEmojis[g2] || ''}`);
      }
    }

    return {
      milhares: [...topMilhares, ...randomMilhares],
      centenas,
      dezenas,
      duquesDezena,
      dupGrupo,
    };
  }, [filtered]);

  const handleCopy = () => {
    if (!jogos) return;
    const text = [
      "🎯 JOGOS GERADOS (Estatísticas)",
      "",
      "📊 MILHARES: " + jogos.milhares.join(", "),
      "📊 CENTENAS: " + jogos.centenas.join(", "),
      "📊 DEZENAS: " + jogos.dezenas.join(", "),
      "📊 DUQUE DEZENA: " + jogos.duquesDezena.join(", "),
      "📊 DUQUE GRUPO: " + jogos.dupGrupo.join(" | "),
    ].join("\n");
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({ title: "Copiado!", description: "Jogos copiados para a área de transferência" });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerate = () => {
    if (filtered.length === 0) {
      toast({ title: "Sem dados", description: "Selecione um período com resultados", variant: "destructive" });
      return;
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!jogos) return;
    setSaving(true);
    try {
      await salvarPalpites({
        source,
        origem: "estatisticas",
        filtros: {
          ...filtrosGeracao,
          total: filtered.length,
          total_resultados_analisados: filtered.length,
          total_sorteios_analisados: new Set(filtered.map((item) => `${item.data}|${item.turno}`)).size,
        },
        palpites: {
          milhares: jogos.milhares,
          centenas: jogos.centenas,
          dezenas: jogos.dezenas,
          duques_dezenas: jogos.duquesDezena,
          duques_grupos: jogos.dupGrupo.map((item) =>
            item.match(/G(\d+)/g)?.map((g) => String(Number(g.slice(1)))).join("-") || ""
          ).filter(Boolean),
        },
      });
      toast({ title: "Jogos salvos", description: "Prontos para conferir os próximos resultados." });
    } catch (error) {
      toast({ title: "Não foi possível salvar", description: error instanceof Error ? error.message : "Tente novamente", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button onClick={handleGenerate} className="gap-2 w-full sm:w-auto">
        <Dices className="h-4 w-4" />
        Gerar Jogos
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Dices className="h-5 w-5" />
              Jogos Gerados pelas Estatísticas
            </DialogTitle>
            <DialogDescription>
              Combinações baseadas nos dados filtrados ({filtered.length} registros)
            </DialogDescription>
          </DialogHeader>

          {jogos && (
            <div className="space-y-4">
              <Section title="🎰 Milhares" items={jogos.milhares} />
              <Section title="💯 Centenas" items={jogos.centenas} />
              <Section title="🔢 Dezenas" items={jogos.dezenas} />
              <Section title="🔗 Duque de Dezena" items={jogos.duquesDezena} />
              <Section title="🐾 Duque de Grupo" items={jogos.dupGrupo} />

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button onClick={handleCopy} variant="outline" className="w-full gap-2">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copiado!" : "Copiar Todos"}
                </Button>
                <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salvar para Conferência
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Section: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <Card>
    <CardHeader className="py-2 px-4">
      <CardTitle className="text-sm">{title}</CardTitle>
    </CardHeader>
    <CardContent className="px-4 pb-3 pt-0">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge key={i} variant="secondary" className="font-mono text-xs">
            {item}
          </Badge>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default GerarJogosEstatisticas;
